import { useState } from "react";
import { Button } from "@design-system/Button/Button";
import type { Align, Side } from "@design-system/overlay/overlayPositioning";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { TooltipBodyProps } from "@design-system/Tooltip/Tooltip.types";
import { useAnatomyMode } from "./componentAnatomyMode";
import {
    BooleanControl,
    PlaygroundSelect,
    TextareaControl,
    TextControl,
} from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import {
    FIGMA_SAMPLE_CONTENT,
    TooltipPlaygroundMatrix,
} from "./TooltipPlaygroundMatrix";
import { TooltipStressSandbox } from "./TooltipStressSandbox";
import styles from "./TooltipPlayground.module.scss";

const sides = [
    "top",
    "bottom",
    "left",
    "right",
] as const satisfies readonly Side[];

const aligns = ["start", "center", "end"] as const satisfies readonly Align[];

/** Builds the body the API allows: a shortcut drops the title and bullets. */
const toTooltipBody = ({
    content,
    title,
    bullets,
    shortcut,
}: {
    content: string;
    title: string;
    bullets: string[];
    shortcut: string;
}): TooltipBodyProps =>
    shortcut === ""
        ? { content, title: title === "" ? undefined : title, bullets }
        : { content, shortcut };

/** Anatomy variant (Figma matrix row) the sandbox content currently draws. */
const anatomyVariantId = (body: TooltipBodyProps) => {
    if (body.shortcut !== undefined) {
        return body.content === "" ? "shortcutOnly" : "shortcut";
    }
    const hasTitle = body.title !== undefined;
    const hasBullets = body.bullets !== undefined && body.bullets.length > 0;
    if (hasTitle && hasBullets) {
        return "titleBullets";
    }
    if (hasTitle) {
        return "title";
    }
    return hasBullets ? "bullets" : "text";
};

/**
 * Tooltip sandbox: every slot, placement and the disabled paths, an
 * always-open switch for screenshots, the Figma matrix and the stress gate.
 */
export const TooltipPlayground = () => {
    const [content, setContent] = useState(FIGMA_SAMPLE_CONTENT);
    const [title, setTitle] = useState("");
    const [bulletsText, setBulletsText] = useState("");
    const [shortcut, setShortcut] = useState("");
    const [side, setSide] = useState<Side>("top");
    const [align, setAlign] = useState<Align>("center");
    const [isDisabled, setIsDisabled] = useState(false);
    const [isTriggerDisabled, setIsTriggerDisabled] = useState(false);
    const [isAlwaysOpen, setIsAlwaysOpen] = useState(false);
    const [isStressSandboxShown, setIsStressSandboxShown] = useState(false);

    const bullets = bulletsText
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line !== "");
    const body = toTooltipBody({
        content: content.trim(),
        title: title.trim(),
        bullets,
        shortcut: shortcut.trim(),
    });
    const anatomy = useAnatomyMode({
        component: "tooltip",
        variantId: anatomyVariantId(body),
        isDisabled: false,
    });
    const liveSummary = `${side} · ${align}${isAlwaysOpen ? " · open" : ""}`;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Content</h2>
                        <TextControl
                            label="Content"
                            value={content}
                            onChange={setContent}
                        />
                        <TextControl
                            label="Title"
                            value={title}
                            onChange={setTitle}
                        />
                        <TextareaControl
                            label="Bullets (one per line)"
                            value={bulletsText}
                            onChange={setBulletsText}
                            rows={3}
                        />
                        <TextControl
                            label="Shortcut"
                            value={shortcut}
                            placeholder="escape, mod+k, g>t"
                            onChange={setShortcut}
                        />
                        <p className={styles.note}>
                            A shortcut drops the title and bullets, as the API
                            does.
                        </p>
                    </section>
                    <section>
                        <h2>Side</h2>
                        <PlaygroundSelect<Side>
                            options={sides}
                            value={side}
                            onChange={setSide}
                        />
                        <h2>Align</h2>
                        <PlaygroundSelect<Align>
                            options={aligns}
                            value={align}
                            onChange={setAlign}
                        />
                    </section>
                    <section>
                        <h2>Features</h2>
                        <BooleanControl
                            label="Disabled tooltip"
                            checked={isDisabled}
                            onChange={setIsDisabled}
                        />
                        <BooleanControl
                            label="Disabled trigger button"
                            checked={isTriggerDisabled}
                            onChange={setIsTriggerDisabled}
                        />
                        <BooleanControl
                            label="Always open (screenshots)"
                            checked={isAlwaysOpen}
                            onChange={setIsAlwaysOpen}
                        />
                        <BooleanControl
                            label="Stress sandbox"
                            checked={isStressSandboxShown}
                            onChange={setIsStressSandboxShown}
                        />
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={
                    anatomy.isActive ? `x-ray · ${liveSummary}` : liveSummary
                }
                belowStage={
                    isStressSandboxShown ? (
                        <TooltipStressSandbox />
                    ) : (
                        <TooltipPlaygroundMatrix />
                    )
                }
                output={JSON.stringify(
                    {
                        Tooltip: {
                            ...body,
                            side,
                            align,
                            disabled: isDisabled,
                            open: isAlwaysOpen || undefined,
                        },
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <Tooltip
                        {...body}
                        side={side}
                        align={align}
                        disabled={isDisabled}
                        open={isAlwaysOpen ? true : undefined}
                    >
                        <Button
                            variant="secondary"
                            disabled={isTriggerDisabled}
                        >
                            Hover me
                        </Button>
                    </Tooltip>
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
